import {
    businessTypeLabels,
    formatEin,
    RegionId,
    supportedBusinessTypes,
} from "@sp-crm/core";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Select } from "components/ui/select";
import React from "react";
import { OfficeNameLookup } from "./use-business-verification";
import {
    FieldHint,
    FieldProblem,
    FieldWarning,
    optionOrNull,
    StepHeading,
} from "./wizard-parts";
import { WizardStepProps } from "./wizard-step-props";

export interface BusinessOffices {
    isMultiOffice: boolean;
    officeIds: RegionId[];
    availableOfficeIds: RegionId[];
    officeName: OfficeNameLookup;
    updating: boolean;
    failed: boolean;
    addOffice: (regionId: RegionId) => void;
    removeOffice: (regionId: RegionId) => void;
}

interface StepBusinessProps extends WizardStepProps {
    offices: BusinessOffices;
}

const OfficeChecklist: React.FC<{ offices: BusinessOffices }> = ({ offices }) => (
    <div>
        <span className="block text-sm font-medium text-gray-700 mb-1">
            Offices this business covers
        </span>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
            {offices.availableOfficeIds.map(regionId => {
                const covered = offices.officeIds.includes(regionId);
                const onlyOffice = covered && offices.officeIds.length === 1;
                return (
                    <Checkbox
                        key={regionId}
                        label={offices.officeName(regionId)}
                        checked={covered}
                        disabled={offices.updating || onlyOffice}
                        onChange={e =>
                            e.target.checked
                                ? offices.addOffice(regionId)
                                : offices.removeOffice(regionId)
                        }
                    />
                );
            })}
        </div>
        <FieldHint>
            Check every office that operates under this EIN. An office with its own EIN
            gets verified separately. A business needs at least one office.
        </FieldHint>
        <FieldProblem
            problem={
                offices.failed
                    ? "We couldn't change this business's offices. Try again in a moment."
                    : null
            }
        />
    </div>
);

const websiteWarning = (website: string): string | null =>
    website.trim() && !/^https:\/\//i.test(website.trim())
        ? "Carriers look for a secure address that starts with https://"
        : null;

export const BusinessFields: React.FC<WizardStepProps> = ({
    details,
    problems,
    onChange,
}) => (
    <>
        <div>
            <Input
                label="Legal business name"
                value={details.legalName}
                onChange={e => onChange({ legalName: e.target.value })}
            />
            <FieldHint>
                Enter it exactly as printed on your IRS letter (CP 575 or 147C), including
                endings like LLC or Inc.
            </FieldHint>
            <FieldProblem problem={problems.legalName ?? null} />
        </div>
        <div>
            <Input
                label="Name customers know you by"
                value={details.doingBusinessAs}
                onChange={e => onChange({ doingBusinessAs: e.target.value })}
            />
            <FieldHint>
                The name on your website and marketing. It can be the same as your legal
                name.
            </FieldHint>
            <FieldProblem problem={problems.doingBusinessAs ?? null} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <Select
                    label="Business type"
                    value={details.businessType ?? ""}
                    onChange={e =>
                        onChange({
                            businessType: optionOrNull(
                                supportedBusinessTypes,
                                e.target.value,
                            ),
                        })
                    }>
                    <option value="">Choose one</option>
                    {supportedBusinessTypes.map(type => (
                        <option key={type} value={type}>
                            {businessTypeLabels[type]}
                        </option>
                    ))}
                </Select>
                <FieldProblem problem={problems.businessType ?? null} />
            </div>
            <div>
                <Input
                    label="EIN"
                    value={details.ein}
                    placeholder="00-0000000"
                    aria-invalid={!!problems.ein}
                    onChange={e => onChange({ ein: formatEin(e.target.value) })}
                />
                <FieldHint>
                    The 9 digit Employer Identification Number on your IRS letter.
                    Don&apos;t enter a Social Security number.
                </FieldHint>
                <FieldProblem problem={problems.ein ?? null} />
            </div>
        </div>
        <div>
            <Input
                label="Website"
                value={details.website}
                placeholder="https://"
                onChange={e => onChange({ website: e.target.value })}
            />
            <FieldHint>
                Carriers visit it. It needs to load without a login and clearly belong to
                this business.
            </FieldHint>
            <FieldWarning warning={websiteWarning(details.website)} />
            <FieldProblem problem={problems.website ?? null} />
        </div>
        <div>
            <Input
                label="Facebook or LinkedIn page (optional)"
                value={details.socialPage}
                placeholder="https://"
                onChange={e => onChange({ socialPage: e.target.value })}
            />
            <FieldHint>Helps carriers recognize you if your website is small.</FieldHint>
        </div>
    </>
);

export const businessHeading = {
    title: "Your business",
    description:
        "Carriers match these details against IRS records, so they need to be exact.",
};

export const StepBusiness: React.FC<StepBusinessProps> = ({
    details,
    problems,
    onChange,
    offices,
}) => (
    <div className="space-y-6">
        <StepHeading {...businessHeading} />
        {offices.isMultiOffice ? <OfficeChecklist offices={offices} /> : null}
        <BusinessFields details={details} problems={problems} onChange={onChange} />
    </div>
);
